import { useState, useMemo, useEffect } from "react";
import { MuiMaterials } from "../@index";
import { createTheme } from '@mui/material/styles';
import * as global from '../data/global';
import {
    DEFAULT_HISTORY_TIME_OF_DAY,
    normalizeTimeOfDayHms,
    resolveDefaultHistoryTimeOfDay,
} from '../data/historyTimeOfDay.js';
import { outlinedActionButtonSx } from './windowChrome/index.js';

const getDatePickerTheme = () => {
    const isLight = global.gw.theme === 'light';
    return createTheme({
        components: {
            MuiButton: {
                styleOverrides: {
                    root: {
                        fontSize: "18px",
                        background: isLight ? '#e0e0e0' : '#2d2d2d',
                        color: isLight ? 'rgb(51, 51, 51)' : 'rgb(244, 204, 142)',
                    }
                }
            },
            MuiIconButton: {
                styleOverrides: {
                    sizeMedium: {
                        color: isLight ? 'rgb(51, 51, 51)' : 'rgb(244, 204, 142)'
                    }
                }
            },
            MuiOutlinedInput: {
                styleOverrides: {
                    root: {
                        fontSize: "18px",
                        background: isLight ? '#ffffff' : '#2d2d2d',
                        color: isLight ? '#333333' : 'rgb(244, 204, 142)'
                    }
                }
            },
            MuiInputLabel: {
                styleOverrides: {
                    root: {
                        fontSize: "18px",
                        background: isLight ? '#ffffff' : '#2d2d2d',
                        color: isLight ? '#000000' : 'rgb(244, 204, 142)',
                        ...(isLight && {
                            '&:not(.MuiInputLabel-shrink)': {
                                top: '50%',
                                transform: 'translateY(-50%)',
                                left: '14px',
                            },
                        }),
                    },
                    shrink: {
                        top: isLight ? '8px' : undefined,
                    },
                }
            },
        }
    });
};

export const DateChanger = (props) => {
    let fd = new Date();
    fd.setDate(fd.getDate() - 5);

    const [fromDate, setFromDate] = useState(fd);
    const [toDate, setToDate] = useState(new Date());
    const [timeOfDay, setTimeOfDay] = useState(() =>
        normalizeTimeOfDayHms(global.gw.selectedSite?.historyTimeOfDay ?? DEFAULT_HISTORY_TIME_OF_DAY)
    );
    const [loading, setLoading] = useState(false);
    const [forceUpdate, setForceUpdate] = useState(0);

    // Helper function to get theme-aware colors
    const getThemeColor = (lightColor, darkColor) => {
        return global.gw.theme === 'light' ? lightColor : darkColor;
    };

    // Listen for theme changes
    useEffect(() => {
        const handleThemeChange = () => {
            setForceUpdate(prev => prev + 1);
        };
        window.addEventListener('themeChanged', handleThemeChange);
        return () => window.removeEventListener('themeChanged', handleThemeChange);
    }, []);

    // Same as Comparison: first Task.RunAt autoscan on Date To's weekday.
    useEffect(() => {
        const syncTimeOfDay = (hms) => {
            if (hms) setTimeOfDay(normalizeTimeOfDayHms(hms));
        };

        const handleHistoryTimeUpdated = (e) => {
            syncTimeOfDay(e?.detail?.timeOfDay ?? global.gw.selectedSite?.historyTimeOfDay);
        };

        window.addEventListener("historyTimeOfDayUpdated", handleHistoryTimeUpdated);

        let cancelled = false;
        (async () => {
            const hms = await resolveDefaultHistoryTimeOfDay(global.gw.selectedSite?.siteGuid, toDate);
            if (!cancelled) syncTimeOfDay(hms);
        })();

        return () => {
            cancelled = true;
            window.removeEventListener("historyTimeOfDayUpdated", handleHistoryTimeUpdated);
        };
    }, [toDate]);

    const datePickerTheme = useMemo(() => getDatePickerTheme(), [forceUpdate]);

    return (
        <div
            className="dateChanger"
            key={`datechanger-${global.gw.theme}-${forceUpdate}`}
            style={{
                display: "none",
                flexDirection: "column",
                background: getThemeColor("rgb(255, 255, 255)", "rgb(45, 45, 45)"),
                borderStyle: "solid",
                borderWidth: "0.75px",
                borderColor: getThemeColor("#cccccc", "#f4cc8e"),
                borderRadius: "5px",
                padding: "20px 43.5px",
                position: "absolute",
                top: "42px",
                left: "6px"
            }}
        >
            <MuiMaterials.LocalizationProvider
                dateAdapter={MuiMaterials.AdapterDateFns}
            >
                <MuiMaterials.ThemeProvider theme={datePickerTheme}>
                    <MuiMaterials.DatePicker
                        label="Date From"
                        className="dateFrom"
                        value={fromDate}
                        sx={{ width: "160px" }}
                        slotProps={{
                            popper: {
                                sx: {
                                    ".MuiDayCalendar-weekDayLabel": {
                                        color: getThemeColor("#333333", "#f4cc8e"),
                                    },
                                    ".MuiPaper-root": {
                                        border: `1px solid ${getThemeColor("#cccccc", "#f4cc8e")}`,
                                        borderRadius: "10px",
                                        background: getThemeColor("#ffffff", "#2d2d2d"),
                                        color: getThemeColor("#333333", "#f4cc8e"),
                                    },
                                    ".MuiButtonBase-root": {
                                        color: getThemeColor("#333333", "#f4cc8e"),
                                    },
                                    ".MuiPickersDay-today": {
                                        borderColor: getThemeColor("#666666", "#f4cc8e"),
                                    },
                                    ".MuiPickersDay-root.Mui-selected": {
                                        background: getThemeColor("#e0e0e0", "black"),
                                        ':hover': {
                                            bgcolor: getThemeColor("#d0d0d0", "black"),
                                        },
                                        ':active': {
                                            bgcolor: getThemeColor("#d0d0d0", "black"),
                                        },
                                    },
                                },
                            },
                        }}
                        onChange={setFromDate}
                    />
                    <MuiMaterials.DatePicker
                        label="Date To"
                        className="reportTime"
                        value={toDate}
                        slotProps={{
                            popper: {
                                sx: {
                                    ".MuiDayCalendar-weekDayLabel": {
                                        color: getThemeColor("#333333", "#f4cc8e"),
                                    },
                                    ".MuiPaper-root": {
                                        border: `1px solid ${getThemeColor("#cccccc", "#f4cc8e")}`,
                                        borderRadius: "10px",
                                        background: getThemeColor("#ffffff", "#2d2d2d"),
                                        color: getThemeColor("#333333", "#f4cc8e"),
                                    },
                                    ".MuiButtonBase-root": {
                                        color: getThemeColor("#333333", "#f4cc8e"),
                                    },
                                    ".MuiPickersDay-today": {
                                        borderColor: getThemeColor("#666666", "#f4cc8e"),
                                    },
                                    ".MuiPickersDay-root.Mui-selected": {
                                        background: getThemeColor("#e0e0e0", "black"),
                                        ':hover': {
                                            bgcolor: getThemeColor("#d0d0d0", "black"),
                                        },
                                        ':active': {
                                            bgcolor: getThemeColor("#d0d0d0", "black"),
                                        },
                                    },
                                },
                            },
                        }}
                        sx={{
                            width: "160px",
                            marginTop: "8px",
                        }}
                        onChange={setToDate}
                    />
                    <MuiMaterials.TextField
                        label="Time of Day"
                        type="time"
                        value={timeOfDay}
                        onChange={(e) => setTimeOfDay(e.target.value)}
                        InputLabelProps={{
                            shrink: true,
                        }}
                        inputProps={{
                            step: 1, // seconds
                        }}
                        sx={{
                            width: "160px",
                            marginTop: "8px",
                            '& .MuiInputBase-input': {
                                fontSize: "16px",
                                padding: "14px 12px",
                            },
                            '& .MuiInputLabel-root': {
                                fontSize: "18px",
                            },
                        }}
                    />
                    <MuiMaterials.LoadingButton
                        type="button"
                        loading={loading}
                        variant="outlined"
                        size="small"
                        sx={{
                            margin: "8px 0px 0px 0px",
                            width: "160px",
                            ...outlinedActionButtonSx(),
                        }}
                        onClick={(e) => {
                            props.applyDates(fromDate, toDate, setLoading, timeOfDay);
                            e.preventDefault();
                            return false;
                        }}
                    >
                        Apply Dates
                    </MuiMaterials.LoadingButton>
                </MuiMaterials.ThemeProvider>
            </MuiMaterials.LocalizationProvider>
        </div>
    );
}

export default DateChanger;